import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Fetch_product_sub_catalogue } from "../../../hooks/APIHooks";
import LogoLoader from "../../../components/LogoLoader/LogoLoader";
import { IoSearchOutline } from "react-icons/io5";

import lazy_loaderwebp from '../../../assets/lazy_loaderwebp.webp';

const SubCatalogueList = () => {
  const { catalogueId } = useParams();
  const navigate = useNavigate();
  const [subCatalogues, setSubCatalogues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [imageLoaded, setImageLoaded] = useState({});
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    const fetchSubCatalogues = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(
          `${Fetch_product_sub_catalogue}?catalogue_id=${catalogueId}`
        );
        const data = await response.json();
        if (data.success) {
          setSubCatalogues(data.data);
        } else {
          throw new Error(data.error || "Failed to fetch sub-catalogues");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSubCatalogues();
  }, [catalogueId]);

  const getImageUrl = (imagePath) => {
    if (!imagePath) {
      return '';
    }
    
    if (imagePath.startsWith('http')) return imagePath;
    
    const cleanPath = imagePath.replace(/^(\.?\/?)?/, '');
    
    // Handle different environments
    let baseUrl;
    const { hostname, port, protocol } = window.location;
    
    if (hostname === 'localhost' && port === '3000') {
      // React dev server on port 3000, APIs on port 80
      baseUrl = `${protocol}//${hostname}`;
    } else if (hostname === 'localhost' && !port) {
      // React build served from same domain as APIs
      baseUrl = window.location.origin;
    } else {
      // Production environment
      baseUrl = window.location.origin;
    }
    
    return `${baseUrl}/apis/${cleanPath}`;
  };

  const handleImageLoad = (id) => {
    setImageLoaded((prev) => ({ ...prev, [id]: true }));
    setImageErrors((prev) => ({ ...prev, [id]: false }));
  };

  const handleImageError = (id) => {
    setImageErrors((prev) => ({ ...prev, [id]: true }));
    setImageLoaded((prev) => ({ ...prev, [id]: true }));
  };

  const handleSubCatalogueClick = (subCatalogueId) => {
    navigate(`/Store/sub-catalogue/products/${subCatalogueId}`);
  };

  const getFallbackContent = (subCatalogue) => {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-200 rounded-lg">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-600 mb-2">
            {subCatalogue.sub_catalogue_name ? subCatalogue.sub_catalogue_name.charAt(0).toUpperCase() : '?'}
          </div>
          <div className="text-xs text-gray-500 px-2">
            {subCatalogue.sub_catalogue_name || 'No Name'}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="w-full overflow-hidden">
      {loading ? (
        <div className="flex justify-center items-center h-[100vh]">
          <LogoLoader />
        </div>
      ) : error ? (
        <div className="text-center text-red-500 py-8">{error}</div>
      ) : subCatalogues.length === 0 ? (
        <div className="text-center text-gray-500 py-8">No sub-catalogues found.</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 p-4">
          {subCatalogues.map((subCatalogue) => (
            <div
              key={subCatalogue.sub_catalogue_id}
              className="relative rounded-lg shadow-md h-[33vh] sm:h-[50vh] cursor-pointer group hover:shadow-lg transition-shadow duration-300 overflow-hidden"
              onClick={() => handleSubCatalogueClick(subCatalogue.sub_catalogue_id)}
            >
              {/* Lazy Loader */}
              <img
                src={lazy_loaderwebp}
                alt="Loading..."
                className="w-full h-full object-cover absolute top-0 left-0 transition-opacity duration-500"
                style={{ opacity: imageLoaded[subCatalogue.sub_catalogue_id] ? 0 : 1 }}
              />
              
              {/* Main Image or Fallback */}
              {subCatalogue.sub_catalogue_image && !imageErrors[subCatalogue.sub_catalogue_id] ? (
                <img
                  src={getImageUrl(subCatalogue.sub_catalogue_image)}
                  alt={subCatalogue.sub_catalogue_name || "Unknown Sub-Catalogue"}
                  loading="lazy"
                  onLoad={() => handleImageLoad(subCatalogue.sub_catalogue_id)}
                  onError={() => handleImageError(subCatalogue.sub_catalogue_id)}
                  className="w-full h-full object-cover transition-opacity duration-500 group-hover:scale-105 transition-transform duration-300"
                  style={{ opacity: imageLoaded[subCatalogue.sub_catalogue_id] ? 1 : 0 }}
                />
              ) : (
                getFallbackContent(subCatalogue)
              )}
              
              {/* Overlay with sub-catalogue info */}
              <div className="absolute bottom-0 w-full text-center bg-gradient-to-t from-black/70 to-transparent p-3">
                <h2 className="text-[14px] font-bold text-white truncate">
                  {subCatalogue.sub_catalogue_name || "Unknown Sub-Catalogue"}
                </h2>
                <p className="text-gray-300 text-xs truncate mt-1">
                  {subCatalogue.description || "No description available"}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default SubCatalogueList;